import { api } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'

/**
 * Elections — platform reference data, not a turnout concept.
 *
 * The backend feature is `election` and the routes are `/api/v1/elections`. `turnout`,
 * and later `survey` and `politics`, reference an election by id and resolve it through
 * `ElectionLookup`; this module is where one is created and advanced.
 */

export interface Election {
  id: string
  code: string
  name: string
  electionYear: number
  electionType: string
  pollingDate: string
  pollsOpenAt: string
  pollsCloseAt: string
  statusCode: string
  scopeUnitId: string | null
  unitPath: string | null
  registrationDeadline: string | null
  transferDeadline: string | null
  gazetteReference: string | null
  closedAt: string | null
  closedBy: string | null
  version: number
  createdAt: string
}

export interface ElectionInput {
  code: string
  name: string
  electionYear: number
  electionType: string
  pollingDate: string
  pollsOpenAt: string
  pollsCloseAt: string
  scopeUnitId?: string | null
  registrationDeadline?: string | null
  transferDeadline?: string | null
  gazetteReference?: string | null
}

export const listElections = (filter: Record<string, QueryValue> = {}) =>
  api.get<Election[]>(`/elections${qs(filter)}`)

export const getElection = (id: string) => api.get<Election>(`/elections/${id}`)

export const createElection = (body: ElectionInput) =>
  api.post<Election>('/elections', body)

export const updateElection = (id: string, body: ElectionInput) =>
  api.put<Election>(`/elections/${id}`, body)

/**
 * Advance to the next phase. **No body** — the server owns the sequence
 * (PLANNED → ORGANISING → ENGAGING → POLLING → CLOSED → RECONCILED) and there are no
 * backward transitions, so there is nothing for the client to choose.
 */
export const advanceElection = (id: string) =>
  api.post<Election>(`/elections/${id}/advance`, undefined)

export const electionPhases = () => api.get<string[]>('/elections/vocabulary/phases')
export const electionTypes = () => api.get<string[]>('/elections/vocabulary/election-types')
